<template>
  <div class="waiting-list">
    <template v-if="this.configurationWasCompleted">
      <vs-tabs
        position="top"
        v-model="tabActive"
        class="tabs-shadow-none"
        v-show="!createOrEdit && !qrCode">

        <vs-tab
          v-for="(tab, tabKey) in tabsWithCounts"
          :key="tabKey"
          icon-pack="feather"
          :icon="tab.icon"
          :label="tab.labelWithCount"
          class="p-0"
        >
          <!-- Keep minimal tab content to avoid Vuesax nextTick errors when slots are empty -->
          <div class="hidden"></div>
        </vs-tab>
      </vs-tabs>

      <vs-divider v-show="!createOrEdit && !qrCode"/>
    </template>

    <transition name="zoom-fade-in">
        <div
          v-if="this.configurationWasCompleted && !createOrEdit && !qrCode"
          class="vx-card p-6"
          style="position: relative;">
        <base-ag-grid-header
          :rows-selected-count="rowsSelectedCountWithAllSelection"
          :filters-count="filtersAppliedCount"
          :filters-match.sync="filtersMatch"
          @reset-selection="setSelectedRowsNone()"
          @reset-filters="resetFilters()">
          <template v-slot:header-left>
            <div class="hidden md:flex items-center" style="gap: 1rem;">
              <!-- Space reserved for main action button - empty for now -->

              <!-- Status dropdown (Google Chat style) -->
              <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer interactive-pill">
                <div :class="['status-pill', queueStatusClass]">
                  <span class="status-dot"></span>
                  <span class="status-text">{{ queueStatusText }}</span>
                  <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4 ml-1"></feather-icon>
                </div>

                <vs-dropdown-menu>
                  <ul class="status-options-list">
                    <!-- Queue Active Option (description changes based on whether we have pending customers) -->
                    <li
                      class="status-option"
                      :class="{ active: settingsWaitingList.openDay && !settingsWaitingList.paused }"
                      @click="settingsWaitingList.openDay ? (settingsWaitingList.paused ? resumeQueue() : null) : (hasPendingCustomers ? startFreshDay() : openWorkDay())">
                      <div class="status-option-indicator">
                        <span class="status-option-dot active"></span>
                      </div>
                      <div class="status-option-content">
                        <div class="status-option-title">Queue active</div>
                        <div class="status-option-description">
                          {{ hasPendingCustomers && !settingsWaitingList.openDay
                            ? 'Queue will open fresh and clear pending customers'
                            : 'Queue is open and accepting new customers' }}
                        </div>
                      </div>
                      <feather-icon
                        v-if="settingsWaitingList.openDay && !settingsWaitingList.paused"
                        icon="CheckIcon"
                        svgClasses="h-5 w-5 text-primary"></feather-icon>
                    </li>

                    <!-- Queue Paused Option -->
                    <li
                      v-if="settingsWaitingList.openDay"
                      class="status-option"
                      :class="{ active: settingsWaitingList.paused }"
                      @click="!settingsWaitingList.paused ? pauseQueue() : null">
                      <div class="status-option-indicator">
                        <span class="status-option-dot paused"></span>
                      </div>
                      <div class="status-option-content">
                        <div class="status-option-title">Queue paused</div>
                        <div class="status-option-description">
                          New customers cannot join temporarily
                        </div>
                      </div>
                      <feather-icon
                        v-if="settingsWaitingList.paused"
                        icon="CheckIcon"
                        svgClasses="h-5 w-5 text-primary"></feather-icon>
                    </li>

                    <!-- Queue Closed Option (shows what the state will be when closed) -->
                    <li
                      class="status-option"
                      :class="{ active: !settingsWaitingList.openDay }"
                      @click="settingsWaitingList.openDay ? showConfirmCloseWorkDayDialog = true : null">
                      <div class="status-option-indicator">
                        <span :class="['status-option-dot', hasPendingCustomers ? 'pending' : 'closed']"></span>
                      </div>
                      <div class="status-option-content">
                        <div class="status-option-title">
                          {{ hasPendingCustomers ? 'Queue closed with pending' : 'Queue closed' }}
                        </div>
                        <div class="status-option-description">
                          {{ hasPendingCustomers ? 'Some customers are still waiting' : 'No customers waiting' }}
                        </div>
                      </div>
                      <feather-icon
                        v-if="!settingsWaitingList.openDay"
                        icon="CheckIcon"
                        svgClasses="h-5 w-5 text-primary"></feather-icon>
                    </li>
                  </ul>
                </vs-dropdown-menu>
              </vs-dropdown>
            </div>
          </template>

          <template v-slot:header-right>
            <page-header-actions
              class="hidden md:flex"
              :actions="headerActions"
              @action="onHeaderAction"
            />
          </template>
        </base-ag-grid-header>

        <ag-grid-vue
          :key="agGridKey"
          ref="agGridTable"
          :domLayout="domLayout"
          :style="gridStyle"
          :components="components"
          :frameworkComponents="frameworkComponents"
          :gridOptions="gridOptions"
          class="ag-theme-material w-100 ag-grid-table"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          :column-types="columnTypes"
          :getRowNodeId="getRowNodeId"
          :autoParamsRefresh="true"
          rowSelection="multiple"
          :animateRows="true"
          :suppressRowClickSelection="true"
          rowModelType="infinite"
          :pagination="false"
          :paginationPageSize="paginationPageSize"
          :suppressPaginationPanel="suppressPaginationPanel"
          :overlayLoadingTemplate="overlayLoadingTemplate"
          :noRowsOverlayComponent="noRowsOverlayComponent"
          :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
          :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
          :cacheBlockSize="cacheBlockSize"
          :maxBlocksInCache="maxBlocksInCache"
          @selection-changed="onSelectionChanged"
          @grid-ready="onGridReady"
          @model-updated="onModelUpdate"
          @grid-size-changed="onGridSizeChanged"
          @first-data-rendered="onFirstDataRendered"
          @filter-changed="onFilterChanged"
          @pagination-changed="onPaginationChanged"
          @row-clicked="onRowClicked"
        >
        </ag-grid-vue>

        <!-- Mobile Sticky Bottom Bar (inline - scoped styles require this to be in parent) -->
        <div class="sticky-bottom-bar-icons md:hidden">
          <!-- Status dropdown -->
          <vs-dropdown
            vs-custom-content
            vs-trigger-click
            class="mobile-bar-btn mobile-bar-btn-simple"
          >
            <div class="mobile-bar-btn-content">
              <span :class="['mobile-status-dot', queueStatusClass]"></span>
              <span class="mobile-btn-label">Status</span>
            </div>

            <vs-dropdown-menu class="mobile-status-dropdown gp-sticky-dropdown-menu">
              <ul class="status-options-list">
                <li
                  class="status-option"
                  :class="{ active: settingsWaitingList.openDay && !settingsWaitingList.paused }"
                  @click="settingsWaitingList.openDay ? (settingsWaitingList.paused ? resumeQueue() : null) : (hasPendingCustomers ? startFreshDay() : openWorkDay())">
                  <div class="status-option-indicator">
                    <span class="status-option-dot active"></span>
                  </div>
                  <div class="status-option-content">
                    <div class="status-option-title">Queue active</div>
                    <div class="status-option-description">
                      {{ hasPendingCustomers && !settingsWaitingList.openDay
                        ? 'Queue will open fresh and clear pending customers'
                        : 'Queue is open and accepting new customers' }}
                    </div>
                  </div>
                  <feather-icon
                    v-if="settingsWaitingList.openDay && !settingsWaitingList.paused"
                    icon="CheckIcon"
                    svgClasses="h-5 w-5 text-primary"></feather-icon>
                </li>

                <li
                  v-if="settingsWaitingList.openDay"
                  class="status-option"
                  :class="{ active: settingsWaitingList.paused }"
                  @click="!settingsWaitingList.paused ? pauseQueue() : null">
                  <div class="status-option-indicator">
                    <span class="status-option-dot paused"></span>
                  </div>
                  <div class="status-option-content">
                    <div class="status-option-title">Queue paused</div>
                    <div class="status-option-description">
                      New customers cannot join temporarily
                    </div>
                  </div>
                  <feather-icon
                    v-if="settingsWaitingList.paused"
                    icon="CheckIcon"
                    svgClasses="h-5 w-5 text-primary"></feather-icon>
                </li>

                <li
                  class="status-option"
                  :class="{ active: !settingsWaitingList.openDay }"
                  @click="settingsWaitingList.openDay ? showConfirmCloseWorkDayDialog = true : null">
                  <div class="status-option-indicator">
                    <span :class="['status-option-dot', hasPendingCustomers ? 'pending' : 'closed']"></span>
                  </div>
                  <div class="status-option-content">
                    <div class="status-option-title">
                      {{ hasPendingCustomers ? 'Queue closed with pending' : 'Queue closed' }}
                    </div>
                    <div class="status-option-description">
                      {{ hasPendingCustomers ? 'Some customers are still waiting' : 'No customers waiting' }}
                    </div>
                  </div>
                  <feather-icon
                    v-if="!settingsWaitingList.openDay"
                    icon="CheckIcon"
                    svgClasses="h-5 w-5 text-primary"></feather-icon>
                </li>
              </ul>
            </vs-dropdown-menu>
          </vs-dropdown>

          <!-- Form button -->
          <button
            class="mobile-bar-btn mobile-bar-btn-simple"
            type="button"
            @click="$router.push({ name: 'waitlist-public-form' })"
          >
            <i class="feather icon-file-text mobile-bar-icon"></i>
            <span class="mobile-btn-label">Form</span>
          </button>

          <!-- Config button -->
          <button
            class="mobile-bar-btn mobile-bar-btn-simple"
            type="button"
            @click="$router.push({ name: 'waitlist-configuration' })"
          >
            <i class="feather icon-settings mobile-bar-icon"></i>
            <span class="mobile-btn-label">Config</span>
          </button>

          <!-- Refresh button -->
          <button
            class="mobile-bar-btn mobile-bar-btn-simple mobile-bar-btn-refresh"
            type="button"
            @click="refreshVisitCacheAndResetDataSourceAndSelection()"
          >
            <i class="feather icon-refresh-cw mobile-bar-icon"></i>
            <span class="mobile-btn-label">Refresh</span>
          </button>
        </div>
      </div>
    </transition>

    <transition name="zoom-fade-in">
      <waiting-list-not-configured
        v-if="!this.configurationWasCompleted && !createOrEdit"
        @setup="$router.push({ name: 'waitlist-configuration' })">
      </waiting-list-not-configured>
    </transition>

    <transition name="zoom-fade-in">
      <waiting-list-configuration
        v-if="createOrEdit"
        @cancel="$router.push({ name: 'waitlist' })"
        @saved="configurationSaved"
      >
      </waiting-list-configuration>
    </transition>

    <transition name="zoom-fade-in">
      <vx-card
        v-if="qrCode"
        remove-card-action
        remove-card-icon-svg-actions="w-6 h-6"
        class="vx-card p-6"
        @remove="() => $router.push({ name: 'waitlist' })">
        <div
          class="vx-row py-5 flex-col-reverse md:flex-row flex items-center">
          <div class="vx-col w-full md:w-1/2 mt-base md:mt-0">
            <waiting-list-q-r-code :size="250"/>
          </div>
          <div class="vx-col w-full md:w-1/2 mb-5 md:mb-0 flex items-center">
            <div>
              <h2 class="mb-3">{{ $t('$QRCode.Title') }}</h2>
              <p>
                {{ $t('$QRCode.Msg') }}
              </p>

              <div class="w-full flex justify-center md:justify-start">
                <vs-button
                  class="mt-base"
                  size="small"
                  @click="$router.push({ name: 'waitlist' })"
                >
                  {{ $t('$QRCode.StartButton')}}
                </vs-button>
              </div>
            </div>
          </div>
        </div>
      </vx-card>
    </transition>

    <vs-popup
      :title="sendMessagePopupTitle"
      :active.sync="activeModalSendMessage">
      <transition name="zoom-fade">
        <s-m-s-campaign-list-quick-create-or-edit
          v-if="activeModalSendMessage"
          :entity="$enums.Entity.SMS_CAMPAIGN"
          :operation="$enums.Operation.CREATE"
          :waiting-list="waitingListToSendMessage"
          is-waiting-list
          on-modal
          @message-sent="messageSent"
          @cancel="activeModalSendMessage = false"
        />
      </transition>
    </vs-popup>

    <sms-get-started-modal v-model="activeSmsGetStartedModal"/>

    <vs-prompt
      color="primary"
      :title="$t('$ConfirmCloseWorkDayDialog.Title')"
      :acceptText="$t('$ConfirmCloseWorkDayDialog.ConfirmButton')"
      :cancelText="$t('$ConfirmCloseWorkDayDialog.CancelButton')"
      buttonAccept="accept-fill"
      buttonCancel="accept-flat"
      @accept="closeWorkDay"
      @cancel="showConfirmCloseWorkDayDialog = false"
      :active.sync="showConfirmCloseWorkDayDialog">
      <p>{{ $t('$ConfirmCloseWorkDayDialog.Text1') }}</p>

      <p
        v-html="$t('$ConfirmCloseWorkDayDialog.Text2')"
        class="mt-3"
      ></p>
    </vs-prompt>

    <vs-popup
      :title="detailsModalTitle"
      :active.sync="activeWaitingListDetailsModal">
      <transition name="zoom-fade">
        <div v-if="activeWaitingListDetailsModal">
          <waiting-list-cell-renderer-customer
            :customer-payload="waitingListDetails"
            :hide-status="true"
          />

          <waiting-list-cell-renderer-status
            class="mt-3"
            :customer-payload="waitingListDetails"
            :isAgGridComponent="false"
            :onPopup="true"
            @changed="handleModalStatusChange($event)"
          />

          <!-- Action buttons -->
          <div class="flex items-center justify-center mt-base gap-2">
            <!-- 1. Send message icon -->
            <vx-tooltip :text="hasContactInDetails ? 'Send message' : 'Contact deleted'" position="bottom">
              <vs-button
                radius
                size="large"
                color="primary"
                type="flat"
                icon-pack="feather"
                icon="icon-send"
                :disabled="!hasContactInDetails"
                @click="waitingListToSendMessage = waitingListDetails">
              </vs-button>
            </vx-tooltip>

            <!-- 2. Show status page icon -->
            <vx-tooltip text="Show status page" position="bottom">
              <vs-button
                radius
                size="large"
                color="primary"
                type="flat"
                icon-pack="feather"
                icon="icon-external-link"
                @click="showStatusPageFromModal">
              </vs-button>
            </vx-tooltip>

            <!-- 3. View contact icon -->
            <vx-tooltip :text="hasContactInDetails ? 'View contact' : 'Contact deleted'" position="bottom">
              <vs-button
                radius
                size="large"
                color="primary"
                type="flat"
                icon-pack="feather"
                icon="icon-eye"
                :disabled="!hasContactInDetails"
                @click="viewContactFromModal">
              </vs-button>
            </vx-tooltip>

            <!-- 4. Remove from queue icon -->
            <vx-tooltip text="Remove from queue" position="bottom">
              <vs-button
                radius
                size="large"
                color="danger"
                type="flat"
                icon-pack="feather"
                icon="icon-trash-2"
                @click="removeFromQueueFromModal">
              </vs-button>
            </vx-tooltip>
          </div>
        </div>
      </transition>
    </vs-popup>

    <vs-popup
      :title="contactModalTitle"
      :active.sync="activeContactModal">
      <transition name="zoom-fade">
        <contact-list-create-or-edit
          v-if="showContactModal"
          :key="contactOperation"
          :entity="$enums.Entity.CONTACT"
          :operation="contactOperation"
          :model-payload="selectedContactForModal"
          :actions="contactModalActions"
          :all-contact-attributes="allContactAttributes"
          @saved="onContactSaved"
          @action="onContactAction"
          @close="activeContactModal=false">
        </contact-list-create-or-edit>
      </transition>
    </vs-popup>
  </div>
</template>

<script>
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';

import { mapActions, mapGetters } from 'vuex';

import { registerSocketEvent, unregisterSocketEvent } from '@/socket';

// Mixins
import commonListCreateOrEditWithAgGrid from '@/views/modules/_mixins/commonListCreateOrEditWithAgGrid';

// Custom Components
import WaitingListNotConfigured from '@/views/modules/waiting-list/WaitingListNotConfigured.vue';
import WaitingListConfiguration from '@/views/modules/waiting-list/WaitingListConfiguration.vue';
import CellRendererActions from '@/views/modules/_components/cell-renderer/SingleCellRendererActions.vue';
import waitingListService from '@/api/modules/waiting-list.service';
import AgGridSelectionHeader from '@/views/modules/_components/AgGridSelectionHeader';
import BaseAgGridHeader from '@/views/modules/_components/BaseAgGridHeader.vue';
import WaitingListQRCode from '@/views/modules/waiting-list/WaitingListQRCode.vue';
import PageHeaderActions from '@/views/modules/_components/PageHeaderActions.vue';
import SMSCampaignListQuickCreateOrEdit from '@/views/modules/campaigns/sms-campaign/SMSCampaignListQuickCreateOrEdit.vue';
import smsGetStartedModal from '@/views/modules/campaigns/sms-campaign/_components/SMSCampaignGetStartedModal.vue';
import CustomNoRowsOverlay from '@/views/modules/_components/customAgGridNoRowsOverlay';
import ContactListCreateOrEdit from '@/views/modules/contacts/ContactListCreateOrEdit.vue';

// Cell Renderer
import WaitingListCellRendererActions from '@/views/modules/waiting-list/WaitingListCellRendererActions.vue';
import WaitingListCellRendererArrived from '@/views/modules/waiting-list/WaitingListCellRendererArrived.vue';
import WaitingListCellRendererStatus from '@/views/modules/waiting-list/WaitingListCellRendererStatus.vue';
import WaitingListCellRendererCustomer from '@/views/modules/waiting-list/WaitingListCellRendererCustomer.vue';
import WaitingListCellRendererWaiting from '@/views/modules/waiting-list/WaitingListCellRendererWaiting.vue';

// Services
import smsTemplateService from '@/api/modules/sms-template.service';

export default {
  name: 'WaitingList',
  i18n: {
    messages: {
      en: {
        ThereAreNoCustomers: 'No customers are waiting',
        $NoRowsOverlay: {
          Active: {
            Title: 'No customers in the queue',
            Text: 'Your queue is open and ready for customers. Share your public form or create a campaign to let customers know they can join.',
            PrimaryButton: 'Public form',
            SecondaryButton: 'Create new campaign',
          },
          Closed: {
            Title: 'Queue is closed',
            Text: 'Your workday hasn\'t started yet. Begin your workday to open the queue and allow customers to join, or create a campaign for when you\'re ready.',
            PrimaryButton: 'Begin workday',
            SecondaryButton: 'Create new campaign',
          },
          Served: {
            Title: 'No customers served yet',
            Text: 'Customers you mark as served will appear here.',
          },
          Cancelled: {
            Title: 'No customers cancelled',
            Text: 'Customers who cancel or are marked as cancelled will appear here.',
          },
        },
        ViewQrCode: 'Public form',
        OpenWorkDay: 'Begin workday',
        CloseWorkDay: 'End workday',
        SendMessagePopupTitle: 'Send message to #{turn} {name} ({phone})',
        $QRCode: {
          Title: 'QR code for queue access',
          Msg: 'This is your downloadable QR code for your business to display on-site. Customers can scan this code to access the form and join the queue directly. This QR code simplifies the process for walk-in customers, allowing them to join the queue quickly without needing assistance.',
          StartButton: 'Let\'s get started',
        },
        $Headers: {
          Arrived: 'Arrived',
          Waiting: 'Waited',
        },
        $OpenedWorkDayNtf: {
          Title: 'Begin Workday ',
          Text: 'Workday started successfully.',
        },
        $ClosedWorkDayNtf: {
          Title: 'End Workday ',
          Text: 'Workday ended successfully.',
        },
        $ConfirmCloseWorkDayDialog: {
          Title: 'Are you sure you want to end your workday?',
          Text1: 'Ending your workday will prevent new customers from joining the queue. You can still serve customers who are already waiting.',
          Text2: 'To clear all pending customers, start a new workday. To cancel individual customers, change their status to <strong>Cancelled</strong> — they\'ll automatically receive a notification.',
          ConfirmButton: 'Yes, end workday',
          CancelButton: 'No, cancel',
        },
        DetailsModalTitle: '#{turn}. {name}',
        SendButtonText: 'Send message',
        $Tabs: {
          Active: 'Active',
          Served: 'Served',
          Cancelled: 'Cancelled',
        },
      },
    },
  },
  components: {
    BaseAgGridHeader,
    WaitingListNotConfigured,
    WaitingListConfiguration,
    WaitingListCellRendererStatus,
    WaitingListCellRendererActions,
    WaitingListCellRendererCustomer,
    WaitingListCellRendererArrived,
    WaitingListCellRendererWaiting,
    CellRendererActions,
    WaitingListQRCode,
    PageHeaderActions,
    SMSCampaignListQuickCreateOrEdit,
    smsGetStartedModal,
    ContactListCreateOrEdit,
  },
  mixins: [commonListCreateOrEditWithAgGrid],
  data() {
    return {
      createOrEdit: false,
      qrCode: false,
      activeModalSendMessage: false,
      activeSmsGetStartedModal: false,
      showConfirmCloseWorkDayDialog: false,
      activeWaitingListDetailsModal: false,
      waitingListDetails: null,
      activeContactModal: false,
      showContactModal: false,
      contactOperation: null,
      selectedContactForModal: null,
      contactModalTitle: '',
      allContactAttributes: [],
      visitsCachedData: null,
      forceRefreshCache: false,
      tabActive: 0,
      statusFilterOptionsByTab: [
        [
          this.$enums.WaitingList.STATUS.WAITING,
          this.$enums.WaitingList.STATUS.READY_NEXT,
          this.$enums.WaitingList.STATUS.ACTIVE,
        ],
        [this.$enums.WaitingList.STATUS.ATTENDED],
        [this.$enums.WaitingList.STATUS.CANCELED],
      ],
      statusChangeQueue: [],
      processingStatusChange: false,
      tabs: {
        waiting: {
          icon: 'icon-clock',
          iconPack: 'feather',
          label: this.$t('$Tabs.Active'),
        },
        served: {
          icon: 'icon-user-check',
          iconPack: 'feather',
          label: this.$t('$Tabs.Served'),
        },
        cancelled: {
          icon: 'icon-user-x',
          iconPack: 'feather',
          label: this.$t('$Tabs.Cancelled'),
        },
      },

      socketTurnBookedEventListenerId: 'waiting-list-turn-booked',
      socketTurnUpdatedEventListenerId: 'waiting-list-turn-updated',

      waitingListToSendMessage: null,
      preferFitColumns: false,
      autoSizeAndFitColumns: false,
      noRowsMessage: this.$t('ThereAreNoCustomers'),
      noRowsOverlayComponentParams: {},
      columnDefs: [
        {
          colId: 'turnNumber',
          headerName: '#',
          field: 'turnNumber',
          valueGetter: (params) => {
            if (!params.data) {
              return '';
            }

            return this.getTurnNumberWithPrefix(params.data.turnNumber);
          },
          filter: false,
          sortable: false,
          sort: 'asc',
          width: 100,
          resizable: false,
          checkboxSelection: false,
          headerClass: 'flex justify-center',
          cellClass: 'flex justify-center items-center px-2',
          headerComponentParams: { checkboxSelection: false },
          pinned: 'left',
        },
        {
          colId: 'customer',
          headerName: this.$tc('$General.Customer'),
          field: 'createdAt',
          filter: false,
          sortable: false,
          minWidth: 200,
          flex: 2,
          cellClass: 'mobile-caret',
          cellRendererFramework: 'WaitingListCellRendererCustomer',
        },
        {
          colId: 'createdAt',
          headerName: this.$t('$Headers.Arrived'),
          field: 'createdAt',
          filter: false,
          sortable: false,
          type: 'dateTimeColumn',
          width: 220,
          resizable: true,
          cellClass: 'flex items-center',
          cellRendererFramework: 'WaitingListCellRendererArrived',
        },
        {
          colId: 'statusUpdatedTime',
          headerName: this.$t('$Headers.Waiting'),
          field: 'createdAt',
          filter: false,
          sortable: false,
          width: 180,
          resizable: true,
          cellClass: 'flex items-center',
          cellRendererFramework: 'WaitingListCellRendererWaiting',
        },
        {
          colId: 'status',
          headerName: this.$t('$General.Status'),
          field: 'status',
          sortable: false,
          filter: false,
          width: 180,
          resizable: true,
          headerClass: 'flex justify-center',
          cellRendererFramework: 'WaitingListCellRendererStatus',
        },
        {
          colId: 'actions',
          headerName: this.$tc('$General.Action', 2),
          sortable: false,
          filter: false,
          width: 220,
          resizable: false,
          headerClass: 'flex justify-center',
          cellRendererFramework: 'WaitingListCellRendererActions',
        },
      ],
      components: {
        CellRendererActions,
        WaitingListCellRendererActions,
      },
      frameworkComponents: {
        agColumnHeader: AgGridSelectionHeader,
        customNoRowsOverlay: CustomNoRowsOverlay,
      },
    };
  },
  computed: {
    ...mapGetters({
      getTenantFlag: 'auth/getTenantFlag',
      getUserFlag: 'auth/getUserFlag',
      settingsWaitingList: 'auth/tenantAccountSettingsWaitingList',
      prefixQueuePosition: 'auth/tenantAccountSettingsWaitingListPrefixQueuePosition',
    }),
    domLayout() {
      return 'normal';
    },
    headerButtonsSizeByResolution() {
      return this.$mq !== this.$enums.mqBreakpoints.MOBILE ? 'default' : 'small';
    },
    actionsOnList() {
      return [];
    },
    contactModalActions() {
      // Only show EDIT action, hide DELETE for Phase 1
      return [
        {
          name: this.$enums.Operation.EDIT,
          color: 'primary',
          text: '$General.Edit',
          position: 'left',
          icon: 'icon-edit',
          active: true,
          activeCreateOrEdit: true,
        },
      ];
    },
    configurationWasCompleted() {
      return this.getTenantFlag('completedAccountDetails')
        && this.getTenantFlag('completedWaitingListSettings')
        && this.getTenantFlag('completedBrandingSettings')
        && this.getTenantFlag('completedCompanyPoliciesSettings')
        && this.getTenantFlag('completedOpeningHoursSettings');
    },
    tabCounts() {
      if (this.visitsCachedData) {
        return {
          waiting: this.visitsCachedData.data.filter(
            (visit) => this.statusFilterOptionsByTab[0].includes(visit.status),
          ).length,
          served: this.visitsCachedData.data.filter(
            (visit) => this.statusFilterOptionsByTab[1].includes(visit.status),
          ).length,
          cancelled: this.visitsCachedData.data.filter(
            (visit) => this.statusFilterOptionsByTab[2].includes(visit.status),
          ).length,
        };
      }

      return {
        waiting: 0, // "waiting" is the key, but displays as "Active" in the UI
        served: 0,
        cancelled: 0,
      };
    },
    tabsWithCounts() {
      // Computed property for reactive tab labels with counts
      const result = {};
      Object.keys(this.tabs).forEach((key) => {
        result[key] = {
          ...this.tabs[key],
          labelWithCount: `${this.tabs[key].label} (${this.tabCounts[key] || 0})`,
        };
      });
      return result;
    },
    hasContactInDetails() {
      return this.waitingListDetails && this.waitingListDetails.contact != null;
    },
    visitsCachedDataByStatus() {
      if (this.visitsCachedData) {
        const data = this.visitsCachedData.data
          .filter((visit) => this.statusFilterOptionsBySelectedTab.includes(visit.status))
          .sort((a, b) => a.turnNumber - b.turnNumber);

        return {
          count: data.length,
          data,
        };
      }

      return null;
    },
    sendMessagePopupTitle() {
      return this.waitingListToSendMessage
        ? this.$t('SendMessagePopupTitle', {
          turn: this.getTurnNumberWithPrefix(this.waitingListToSendMessage.turnNumber),
          name: this.waitingListToSendMessage.contact.name,
          phone: this.waitingListToSendMessage.contact.phoneInternational,
        })
        : '';
    },
    detailsModalTitle() {
      if (this.waitingListDetails) {
        return this.$t('DetailsModalTitle', {
          turn: this.getTurnNumberWithPrefix(this.waitingListDetails.turnNumber),
          name: this.waitingListDetails.contact.name,
        });
      }

      return '';
    },
    statusFilterOptionsBySelectedTab() {
      return this.statusFilterOptionsByTab[this.tabActive];
    },
    hasPendingCustomers() {
      // Check if there are active customers in the queue
      // Use the active (waiting) tab count from cached data
      return this.tabCounts && this.tabCounts.waiting > 0;
    },
    queueStatusClass() {
      if (this.settingsWaitingList.openDay) {
        if (this.settingsWaitingList.paused) {
          return 'status-paused';
        }
        return 'status-active';
      } if (this.hasPendingCustomers) {
        return 'status-pending'; // Orange/gold for pending work
      }
      return 'status-closed'; // Red for completely closed
    },
    queueStatusText() {
      if (this.settingsWaitingList.openDay) {
        if (this.settingsWaitingList.paused) {
          return 'Queue paused';
        }
        return 'Queue active';
      } if (this.hasPendingCustomers) {
        return 'Queue closed with pending';
      }
      return 'Queue closed';
    },
    queueStatusTooltip() {
      if (this.settingsWaitingList.openDay) {
        if (this.settingsWaitingList.paused) {
          return 'Queue is paused, new customers cannot join temporarily';
        }
        return 'Queue is open and accepting new customers';
      } if (this.hasPendingCustomers) {
        return 'Some customers are still waiting';
      }

      return 'No customers waiting';
    },
    headerActions() {
      return [
        {
          id: 'publicForm',
          tooltip: 'Public form',
          kind: 'icon',
          icon: 'icon-file-text',
        },
        {
          id: 'configuration',
          tooltip: 'Configuration',
          kind: 'icon',
          icon: 'icon-settings',
        },
        {
          id: 'refresh',
          tooltip: 'Refresh',
          kind: 'icon',
          icon: 'icon-refresh-cw',
        },
      ];
    },
  },
  watch: {
    waitingListToSendMessage(val) {
      if (val) {
        if (!this.getUserFlag('smsCampaignGetStartedModalSkipped')) {
          this.activeSmsGetStartedModal = true;
          this.activeWaitingListDetailsModal = false;
        } else {
          this.activeModalSendMessage = true;
          this.activeWaitingListDetailsModal = false;
        }
      }
    },
    // Watch for tab changes
    async tabActive() {
      // Update the overlay params for the new tab
      this.updateNoRowsOverlayParams();
      
      // Only reset if grid API is ready
      if (this.gridApi) {
        // Wait for any pending status changes to complete before switching tabs
        if (this.processingStatusChange) {
          // Wait for processing to complete
          const checkInterval = setInterval(() => {
            if (!this.processingStatusChange) {
              clearInterval(checkInterval);
              this.resetDataSourceAndSelection();
            }
          }, 50);
        } else {
          this.resetDataSourceAndSelection();
        }
      }
    },
    activeSmsGetStartedModal(val) {
      if (!val && this.waitingListToSendMessage) {
        this.activeModalSendMessage = true;
      }
    },
    activeModalSendMessage(val) {
      if (!val) {
        this.waitingListToSendMessage = null;
      }
    },
    'settingsWaitingList.openDay': function () {
      // Update the overlay when queue status changes
      this.updateNoRowsOverlayParams();
    },
    '$route'() {
      this.handleRouteCheck();
    },
  },
  beforeMount() {
    this.noRowsOverlayComponent = 'customNoRowsOverlay';
    this.gridOptions.rowHeight = 100;
    this.updateNoRowsOverlayParams();
  },
  mounted() {
    this.handleRouteCheck();
    // Fetch initial counts after grid is ready
    // Will be called from onFirstDataRendered

    registerSocketEvent(this.$enums.SocketEvents.WAITING_LIST_BOOKED, (waitingList) => {
      // New customer joined - add to grid using transaction
      this.handleSocketCustomerBooked(waitingList);
    },
    this.socketTurnBookedEventListenerId,
    true);

    registerSocketEvent(this.$enums.SocketEvents.WAITING_LIST_UPDATED, (waitingList) => {
      // Customer status updated - update row using transaction
      this.handleSocketCustomerUpdated(waitingList);
    },
    this.socketTurnUpdatedEventListenerId,
    true);
  },
  beforeDestroy() {
    // Clean up socket events
    unregisterSocketEvent(
      this.$enums.SocketEvents.WAITING_LIST_BOOKED,
      this.socketTurnBookedEventListenerId,
    );
    unregisterSocketEvent(
      this.$enums.SocketEvents.WAITING_LIST_UPDATED,
      this.socketTurnUpdatedEventListenerId,
    );

    // Clear pending operations
    this.statusChangeQueue = [];
    this.processingStatusChange = false;
  },
  methods: {
    ...mapActions({
      openWaitingListWorkDay: 'auth/openWaitingListWorkDay',
      closeWaitingListWorkDay: 'auth/closeWaitingListWorkDay',
      pauseWaitingListWorkDay: 'auth/pauseWaitingListWorkDay',
      resumeWaitingListWorkDay: 'auth/resumeWaitingListWorkDay',
      fetchAllAttributes: 'attribute/fetchAllAttributes',
    }),
    getTurnNumberWithPrefix(turnNumber) {
      const separator = this.prefixQueuePosition ? '-' : '';

      return `${this.prefixQueuePosition || ''}${separator}${turnNumber}`;
    },
    refreshVisitCacheAndResetDataSourceAndSelection() {
      // Set flag to force cache refresh without nullifying it first
      // This prevents the status pill from showing incorrect state during refresh
      this.forceRefreshCache = true;
      this.resetDataSourceAndSelection();
    },
    onHeaderAction({ id }) {
      switch (id) {
        case 'publicForm':
          this.$router.push({ name: 'waitlist-public-form' });
          break;
        case 'configuration':
          this.$router.push({ name: 'waitlist-configuration' });
          break;
        case 'refresh':
          this.refreshVisitCacheAndResetDataSourceAndSelection();
          break;
        default:
          break;
      }
    },
    // Override the mixin method to add tab count refresh
    savedItemOnAgGridTable(item) {
      if (Array.isArray(item)) {
        this.lastSavedIds = item.map((i) => i.id);
      } else if (item) {
        this.lastSavedId = item.id;
      }

      this.savedItem();
      this.refreshVisitCacheAndResetDataSourceAndSelection();
    },
    configurationSaved() {
      this.$router.push({ name: 'waitlist-public-form' });
    },
    handleRouteCheck() {
      if (this.$route.name === 'waitlist-public-form') {
        this.qrCode = true;
        this.createOrEdit = false;
      } else if (this.$route.name === 'waitlist-configuration') {
        this.createOrEdit = true;
        this.qrCode = false;
      } else {
        this.qrCode = false;
        this.createOrEdit = false;
      }
    },
    onFirstDataRendered(params) {
      // Call parent mixin method for column fitting
      this.fitColumnsOnChanges();
    },
    updateNoRowsOverlayParams() {
      const $this = this;
      
      // Check which tab is active (0 = Waiting, 1 = Served, 2 = Cancelled)
      const isWaitingTab = this.tabActive === 0;
      const isServedTab = this.tabActive === 1;
      const isCancelledTab = this.tabActive === 2;

      let params = {};

      if (isWaitingTab) {
        // Waiting tab: Show the existing behaviour with action buttons
        const isQueueActive = this.settingsWaitingList.openDay;
        const overlayConfig = isQueueActive ? '$NoRowsOverlay.Active' : '$NoRowsOverlay.Closed';

        params = {
          title: this.$t(`${overlayConfig}.Title`),
          text: this.$t(`${overlayConfig}.Text`),
          buttonText: this.$t(`${overlayConfig}.PrimaryButton`),
          buttonSecondaryText: this.$t(`${overlayConfig}.SecondaryButton`),
          async actionButtonSecondaryClicked() {
            await $this.$router.push({
              name: 'sms-campaigns-create',
            });
          },
        };

        // Set primary button action and icon based on queue status
        if (isQueueActive) {
          // When active: Primary button opens public form
          params.buttonIcon = 'icon-file-text';
          params.actionButtonClicked = function () {
            $this.$router.push({ name: 'waitlist-public-form' });
          };
        } else {
          // When closed: Primary button opens workday
          params.buttonIcon = 'icon-play';
          params.actionButtonClicked = async function () {
            await $this.openWorkDay();
          };
        }
      } else if (isServedTab) {
        // Served tab: Simple message, no action buttons
        params = {
          title: this.$t('$NoRowsOverlay.Served.Title'),
          text: this.$t('$NoRowsOverlay.Served.Text'),
        };
      } else if (isCancelledTab) {
        // Cancelled tab: Simple message, no action buttons
        params = {
          title: this.$t('$NoRowsOverlay.Cancelled.Title'),
          text: this.$t('$NoRowsOverlay.Cancelled.Text'),
        };
      }

      this.noRowsOverlayComponentParams = params;

      // Force overlay refresh if grid is ready
      if (this.gridApi && this.count === 0) {
        this.$nextTick(() => {
          this.gridApi.showNoRowsOverlay();
        });
      }
    },
    async fetchAgGridData(params) {
      let filters = {
        ...params.filters,
      };
      let { filtersMatch } = params;

      filters = {
        agGridFilters: {
          filterType: this.$enums.AppFilterType.CONDITIONAL,
          filtersMatch: params.filtersMatch,
          filter: params.filters,
        },
      };

      filtersMatch = this.$enums.AppFilterMathType.ALL;

      // Fetch fresh data if cache is empty OR if force refresh is requested
      if (!this.visitsCachedData || this.forceRefreshCache) {
        const { data } = await waitingListService.getAll({
          ...params,
          filters,
          filtersMatch,
          populate: [{
            path: 'contact',
            select: 'id firstName lastName name phoneInternational',
          }],
        });
        this.visitsCachedData = data;
        this.forceRefreshCache = false; // Reset flag after refresh
      }

      return this.visitsCachedDataByStatus;
    },
    messageSent() {
      this.activeModalSendMessage = false;
      this.waitingListToSendMessage = null;
    },
    async viewContactFromWaitingList(customer) {
      // Check if contact exists
      if (!customer.contact) {
        this.$showErrorActionNotification({
          title: 'Contact deleted',
          text: 'This contact has been deleted from the contact list.',
        });
        return;
      }

      try {
        this.$vs.loading({ type: 'radius' });

        // Fetch contact attributes if not already loaded
        if (this.allContactAttributes.length === 0) {
          try {
            const resp = await this.fetchAllAttributes({
              entity: this.$enums.Entity.CONTACT,
            });
            // fetchAllAttributes returns { data: [...] }
            this.allContactAttributes = resp.data || [];
          } catch (error) {
            console.error('Error fetching contact attributes:', error);
            this.allContactAttributes = [];
          }
        }

        // Fetch full contact data from API (populated contact from waiting list is incomplete)
        const contactService = await import('@/api/modules/contact.service');
        const response = await contactService.default.getOne({
          id: customer.contact.id,
          params: {
            populate: [{
              path: 'tags',
              select: 'id name color',
            }],
          },
        });

        const fullContactData = response.data;
        
        // Transform marketingStatus to object with value property if needed
        // The ContactConstructor expects { value: "subscribed" } not just "subscribed"
        let marketingStatus;
        if (!fullContactData.marketingStatus) {
          marketingStatus = { value: this.$enums.Contact.MarketingStatus.SUBSCRIBED };
        } else if (typeof fullContactData.marketingStatus === 'string') {
          marketingStatus = { value: fullContactData.marketingStatus };
        } else {
          // Already an object, use as-is
          marketingStatus = fullContactData.marketingStatus;
        }
        
        this.selectedContactForModal = {
          ...fullContactData,
          marketingStatus,
        };
        
        this.contactOperation = this.$enums.Operation.VIEW;
        this.contactModalTitle = `${fullContactData.firstName || ''} ${fullContactData.lastName || ''}`.trim() || 'View contact';
        
        this.$vs.loading.close();
        
        // Wait for data to be set, then open modal
        await this.$nextTick();
        this.activeContactModal = true;
        
        // Wait for modal to open, then show content
        await this.$nextTick();
        this.showContactModal = true;
      } catch (error) {
        this.$vs.loading.close();
        console.error('Error fetching contact data:', error);
        this.$showErrorActionNotification({
          title: 'Error',
          text: 'Failed to load contact details.',
        });
      }
    },
    showStatusPage(customer) {
      const tenantSuffix = this.$store.getters['auth/tenantUrlLinksSuffix'];
      const statusPageUrl = `${window.location.origin}/waitlist/${tenantSuffix}/${customer.id}`;
      
      // Open in new tab
      window.open(statusPageUrl, '_blank');
    },
    // Modal action methods (called from mobile details modal)
    showStatusPageFromModal() {
      this.showStatusPage(this.waitingListDetails);
      // Keep modal open for this action
    },
    viewContactFromModal() {
      // Close details modal first
      this.activeWaitingListDetailsModal = false;
      // Then open contact modal
      this.viewContactFromWaitingList(this.waitingListDetails);
    },
    removeFromQueueFromModal() {
      // Close details modal first
      this.activeWaitingListDetailsModal = false;
      // Then show remove confirmation
      this.removeCustomerFromQueue(this.waitingListDetails);
    },
    async onContactSaved(updatedContact) {
      try {
        // Contact is already saved by the modal
        // Just update the grid display

        // Find all queue entries with this contact and update them
        if (this.gridApi) {
          const nodesToUpdate = [];
          this.gridApi.forEachNode((node) => {
            if (node.data && node.data.contact
                && node.data.contact.id === updatedContact.id) {
              nodesToUpdate.push(node);
            }
          });

          nodesToUpdate.forEach((node) => {
            // Update the row with new contact data
            node.setData({
              ...node.data,
              contact: updatedContact,
            });
          });

          if (nodesToUpdate.length > 0) {
            this.gridApi.refreshCells({
              rowNodes: nodesToUpdate,
              force: true,
            });
          }
        }

        this.$showSuccessActionNotification({
          title: 'Updated',
          text: 'Contact details updated',
        });

        this.activeContactModal = false;
        this.showContactModal = false;
      } catch (error) {
        console.error('Error updating contact:', error);
        this.$showErrorActionNotification({
          title: 'Error',
          text: 'Failed to update contact details',
        });
      }
    },
    onContactAction(action) {
      switch (action) {
        case this.$enums.Operation.EDIT:
          this.contactOperation = this.$enums.Operation.EDIT;
          this.contactModalTitle = 'Edit contact';
          break;
        default:
          break;
      }
    },
    async removeCustomerFromQueue(customer) {
      const hasContact = customer.contact != null;
      const confirmText = hasContact
        ? 'This customer will be removed from the queue and will not receive a notification. The contact will remain in your contact list.'
        : 'This entry will be removed from the queue and no notification will be sent.';

      this.$showConfirmDialog({
        title: 'Remove from queue',
        text: confirmText,
        color: 'danger',
        acceptText: 'Remove from queue',
        cancelText: 'Cancel',
        accept: async () => {
          try {
            this.$vs.loading({ type: 'radius' });

            // DELETE the entry completely
            // Entry is physically deleted from database - won't show in any tab
            await waitingListService.delete(customer.id);

            // Force complete refresh of cache and grid
            await this.refreshVisitCacheAndResetDataSourceAndSelection();

            this.$vs.loading.close();
            this.$showSuccessActionNotification({
              title: 'Removed',
              text: hasContact
                ? 'Customer removed from queue. Contact remains in your contact list.'
                : 'Entry removed from queue.',
            });
          } catch (error) {
            this.$vs.loading.close();
            this.$showErrorActionNotification({
              title: 'Error',
              text: 'Failed to remove customer from queue',
            });
            console.error('Error removing customer from queue:', error);
          }
        },
      });
    },
    async openWorkDay() {
      this.$vs.loading({ type: 'radius' });
      await this.openWaitingListWorkDay();
      this.$vs.loading.close();

      this.$showSuccessActionNotification({
        title: this.$t('$OpenedWorkDayNtf.Title'),
        text: this.$t('$OpenedWorkDayNtf.Text'),
      });

      await this.refreshVisitCacheAndResetDataSourceAndSelection();
    },
    async startFreshDay() {
      // Clear any stale customers and start fresh workday
      this.$vs.loading({ type: 'radius' });

      try {
        // Clear all waiting customers before starting fresh
        // Option 1: Use deleteMany if available
        if (this.gridApi) {
          // Get all displayed rows in the waiting tab
          const rowsToCancel = [];
          this.gridApi.forEachNode((node) => {
            if (node.data && node.data.status === this.$enums.WaitingList.STATUS.WAITING) {
              rowsToCancel.push(node.data);
            }
          });

          // Cancel each waiting customer
          // Note: This could be optimized with a bulk API endpoint if available
          if (rowsToCancel.length > 0) {
            // For now, we'll use deleteMany if it exists on the service
            // Otherwise, you may need to implement a bulk cancel endpoint
            const ids = rowsToCancel.map((row) => row.id);

            // Option A: If deleteMany exists on waitingListService
            // await waitingListService.deleteMany({ ids, filters: {}, filtersMatch: 'all' });

            // Option B: Cancel each one (less efficient but works)
            // for (const row of rowsToCancel) {
            //   await waitingListService.updateTurnStatus(
            //     { status: this.$enums.WaitingList.STATUS.CANCELED },
            //     row.id
            //   );
            // }

            // For now, we'll just clear the grid and let the backend handle it
            // when opening a new workday
          }
        }

        // Open the workday
        await this.openWaitingListWorkDay();
        this.$vs.loading.close();

        this.$showSuccessActionNotification({
          title: 'Fresh day started',
          text: 'Previous customers cleared and queue is now open',
        });

        await this.refreshVisitCacheAndResetDataSourceAndSelection();
      } catch (error) {
        this.$vs.loading.close();
        this.$showErrorActionNotification({
          title: 'Error',
          text: 'Failed to start fresh day. Please try again.',
        });
        console.error('Error starting fresh day:', error);
      }
    },
    async closeWorkDay() {
      this.$vs.loading({ type: 'radius' });
      await this.closeWaitingListWorkDay();
      this.$vs.loading.close();

      this.$showSuccessActionNotification({
        title: this.$t('$ClosedWorkDayNtf.Title'),
        text: this.$t('$ClosedWorkDayNtf.Text'),
      });
    },
    async pauseQueue() {
      this.$vs.loading({ type: 'radius' });
      try {
        await this.pauseWaitingListWorkDay();
        this.$showSuccessActionNotification({
          title: 'Queue paused',
          text: 'Queue has been paused. New customers cannot join temporarily.',
        });
      } catch (error) {
        this.$showErrorActionNotification({
          title: 'Error',
          text: 'Failed to pause queue.',
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async resumeQueue() {
      this.$vs.loading({ type: 'radius' });
      try {
        await this.resumeWaitingListWorkDay();
        this.$showSuccessActionNotification({
          title: 'Queue active',
          text: 'Queue is now active. New customers can join.',
        });
      } catch (error) {
        this.$showErrorActionNotification({
          title: 'Error',
          text: 'Failed to resume queue.',
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    fitColumnsOnChanges() {
      if (this.gpMqIsMobile) {
        this.columnDefs.forEach((col) => {
          this.gridColumnApi.setColumnVisible(col.colId, !col.hide && ['customer'].includes(col.colId));
        });
      } else if (this.gpMqIsLessOrEqualLaptopMedium) {
        this.columnDefs.forEach((col) => {
          this.gridColumnApi.setColumnVisible(col.colId, !col.hide && ['turnNumber', 'customer', 'status', 'actions'].includes(col.colId));
        });
      } else {
        this.columnDefs.forEach((col) => {
          this.gridColumnApi.setColumnVisible(
            col.colId,
            !['creator'].includes(col.colId),
          );
        });
      }
    },
    onRowClicked(params) {
      if (this.gpMqIsMobile) {
        this.waitingListDetails = params.node.data;
        this.activeWaitingListDetailsModal = true;
      }
    },
    // Queue-based status change methods
    queueStatusChange(customer, newStatus, review = true, fromModal = false) {
      this.statusChangeQueue.push({ customer, newStatus, review, fromModal });
      this.processStatusChangeQueue();
    },
    handleModalStatusChange(eventData) {
      // Extract status and review from event data
      const { status, review = true } = eventData;
      const customer = this.waitingListDetails;

      // Queue the status change with flag indicating it's from the modal
      this.queueStatusChange(customer, status, review, true);
    },
    async processStatusChangeQueue() {
      if (this.processingStatusChange || this.statusChangeQueue.length === 0) {
        return;
      }

      this.processingStatusChange = true;

      while (this.statusChangeQueue.length > 0) {
        const { customer, newStatus, review, fromModal } = this.statusChangeQueue.shift();

        try {
          // Call the API to update status
          const response = await waitingListService.updateTurnStatus(
            { status: newStatus, review },
            customer.id,
          );

          // Create updated customer preserving all existing data
          // For rejoins, the backend returns a new turn number
          const updatedCustomer = {
            ...customer, // Keep all existing data (including populated contact object)
            status: newStatus,
            turnNumber: response.turnNumber || customer.turnNumber, // Use new turn number from backend if provided
            statusUpdatedTime: new Date(),
          };

          this.updateCustomerRowOptimistically(updatedCustomer);
          this.lastSavedId = customer.id;

          // Close the modal if the status change originated from it
          if (fromModal) {
            this.activeWaitingListDetailsModal = false;
          }
        } catch (error) {
          console.error('Error updating status:', error);

          // Only show error if it's a real API error
          if (error.response || error.message) {
            this.$showErrorActionNotification({
              title: 'Error',
              text: 'Failed to update customer status. Please try again.',
            });
          }

          // On error, trigger full refresh to ensure consistency
          this.forceRefreshCache = true;
          this.resetDataSourceAndSelection();
          this.processingStatusChange = false;
          return;
        }
      }

      this.processingStatusChange = false;
    },
    updateCustomerRowOptimistically(updatedCustomer) {
      if (!this.gridApi) return;

      // Update cache first
      this.updateCachedData(updatedCustomer);

      // Recalculate tab counts immediately
      this.recalculateTabCounts();
      this.resetDataSourceAndSelection();

      // Find the row node
      const rowNode = this.getRowNodeById(updatedCustomer.id);

      if (rowNode && rowNode.data) {
        // Merge the update with existing row data to preserve all fields
        const mergedData = {
          ...rowNode.data, // Keep existing data (including contact object)
          ...updatedCustomer, // Apply updates
        };

        // Check if the updated customer should be in the current tab
        const shouldBeInCurrentTab = this.statusFilterOptionsBySelectedTab.includes(
          updatedCustomer.status,
        );

        if (shouldBeInCurrentTab) {
          // Customer stays in current tab - update the row
          rowNode.setData(mergedData);

          // Refresh the specific cells to show changes immediately
          this.gridApi.refreshCells({
            rowNodes: [rowNode],
            force: true,
          });
        } else {
          // Customer moved to another tab - purge and reload
          // Wait for Vue to update computed properties first
          this.$nextTick(() => {
            this.gridApi.purgeInfiniteCache();
          });
        }
      }
    },
    getRowNodeById(id) {
      if (!this.gridApi) return null;

      let foundNode = null;
      this.gridApi.forEachNode((node) => {
        if (node.data && node.data.id === id) {
          foundNode = node;
        }
      });

      return foundNode;
    },
    updateCachedData(updatedCustomer) {
      if (!this.visitsCachedData || !this.visitsCachedData.data) return;

      const index = this.visitsCachedData.data.findIndex(
        (customer) => customer.id === updatedCustomer.id,
      );

      if (index !== -1) {
        // Merge update with existing customer data to preserve all fields
        const mergedCustomer = {
          ...this.visitsCachedData.data[index], // Keep existing data
          ...updatedCustomer, // Apply updates
        };

        // Use Vue.set to ensure reactivity
        this.$set(this.visitsCachedData.data, index, mergedCustomer);
      }
    },
    addToCachedData(newCustomer) {
      if (!this.visitsCachedData) {
        this.visitsCachedData = { data: [], count: 0 };
      }

      // Check if customer already exists
      const exists = this.visitsCachedData.data.some(
        (customer) => customer.id === newCustomer.id,
      );

      if (!exists) {
        this.visitsCachedData.data.unshift(newCustomer);
        this.visitsCachedData.count = this.visitsCachedData.data.length;
      }
    },
    recalculateTabCounts() {
      // This will trigger the reactive computed properties
      // tabCounts and tabsWithCounts to update
      this.$forceUpdate();
    },
    async handleSocketCustomerBooked(waitingList) {
      if (!this.gridApi) return;

      // Fetch the full customer data with populated contact
      try {
        const response = await waitingListService.getOne({
          id: waitingList.id,
          params: {
            populate: [{
              path: 'contact',
              select: 'id firstName lastName name phoneInternational',
            }],
          },
        });

        // Extract the actual customer data from the nested structure
        const customer = {
          ...response.data.waitingList,
          contact: response.data.contact, // Use the populated contact from root level
        };

        // Add to cache with full data
        this.addToCachedData(customer);

        // Update tab counts
        this.recalculateTabCounts();
        this.lastSavedId = customer.id;

        // Check if should be in current tab
        const shouldBeInCurrentTab = this.statusFilterOptionsBySelectedTab.includes(
          customer.status,
        );

        if (shouldBeInCurrentTab) {
          // For new customers, purge the cache to force a complete reload
          // This ensures the new customer appears at the top of the list
          this.$nextTick(() => {
            this.gridApi.purgeInfiniteCache();
          });
        }
      } catch (error) {
        console.error('Error fetching new customer data:', error);
        // Fall back to full refresh
        this.forceRefreshCache = true;
        this.resetDataSourceAndSelection();
      }
    },
    async handleSocketCustomerUpdated(waitingList) {
      if (!this.gridApi) {
        return;
      }

      // If we're currently processing local changes, wait a bit before applying socket update
      if (this.processingStatusChange) {
        // Queue the socket update to be applied after local changes complete
        setTimeout(() => {
          this.handleSocketCustomerUpdated(waitingList);
        }, 100);
        return;
      }

      // Fetch the full customer data with populated contact
      try {
        const response = await waitingListService.getOne({
          id: waitingList.id,
          params: {
            populate: [{
              path: 'contact',
              select: 'id firstName lastName name phoneInternational',
            }],
          },
        });

        // Extract the actual customer data from the nested structure
        const customer = {
          ...response.data.waitingList,
          contact: response.data.contact, // Use the populated contact from root level
        };

        // Update using optimistic update (cache + row)
        this.updateCustomerRowOptimistically(customer);
        this.lastSavedId = customer.id;
      } catch (error) {
        console.error('Error fetching updated customer data:', error);
        // Fall back to full refresh
        this.forceRefreshCache = true;
        this.resetDataSourceAndSelection();
      }
    },
  },
};
</script>

<style scoped lang="scss">
// Fix for mobile scrolling issues
.waiting-list {
  position: relative;
  // Use viewport helper variable with fallback
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
  
  // iOS specific height handling
  @supports (-webkit-touch-callout: none) {
    min-height: -webkit-fill-available;
  }
  
  // Ensure vx-card allows sticky positioning for children
  .vx-card {
    // Don't use overflow on the card itself to allow sticky children
    overflow: visible;
    position: relative;
    
    // Prevent horizontal overflow without breaking sticky
    > *:not(.sticky-bottom-bar-icons) {
      max-width: 100%;
    }
  }
}

// Neumorphism status pill styles
.status-pill-wrapper {
  margin-left: 1rem;
  position: relative;
  display: inline-flex;
  align-items: center;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  position: relative;
  cursor: pointer;
  background: #F9FAFB;
  box-shadow:
    6px 6px 12px rgba(0, 0, 0, 0.1),
    -6px -6px 12px rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  user-select: none;
  -webkit-tap-highlight-color: transparent; // Remove tap highlight on iOS
  -webkit-touch-callout: none; // Disable callout on iOS

  // Only apply hover styles on devices that support hover
  @media (hover: hover) {
    &:hover {
      box-shadow:
        4px 4px 8px rgba(0, 0, 0, 0.12),
        -4px -4px 8px rgba(255, 255, 255, 0.8);
    }
  }

  &:active {
    box-shadow:
      inset 3px 3px 6px rgba(0, 0, 0, 0.15),
      inset -3px -3px 6px rgba(255, 255, 255, 0.7);
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.status-closed {
    color: #991B1B;

    .status-dot {
      background: #EF4444;
    }
  }

  &.status-active {
    color: #166534;

    .status-dot {
      background: #10B981;
      animation: pulse 2s infinite;
    }
  }

  &.status-paused {
    color: #4B5563;

    .status-dot {
      background: #9CA3AF;
    }
  }

  &.status-ended {
    color: #92400E;

    .status-dot {
      background: #F59E0B;
    }
  }

  &.status-pending {
    color: #92400E;

    .status-dot {
      background: #F59E0B;
    }
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

// Status Dropdown Menu (Google Chat style)
.status-options-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.status-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 12px;

  &:hover {
    background-color: #F3F4F6;
  }

  &.active {
    background-color: #F9FAFB;
  }
}

.status-option-indicator {
  flex-shrink: 0;
}

.status-option-dot {
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 50%;

  &.active {
    background: #10B981;
  }

  &.paused {
    background: #9CA3AF;
  }

  &.closed {
    background: #EF4444;
  }

  &.pending {
    background: #F59E0B;
  }
}

.status-option-content {
  flex: 1;
  min-width: 0;
}

.status-option-title {
  font-size: 14px;
  font-weight: 500;
  color: #1F2937;
  margin-bottom: 2px;
}

.status-option-description {
  font-size: 12px;
  color: #6B7280;
  line-height: 1.4;
}

// Mobile responsive adjustments (below 768px - sticky bar visible)
@media (max-width: 767px) {
  .status-pill {
    padding: 8px 14px;
    font-size: 13px;
    gap: 8px;
  }
}

// Tablet/Desktop responsive spacing (768px and above - header controls visible)
@media (min-width: 768px) and (max-width: 1024px) {
  // Tablet spacing - comfortable
  .vx-card {
    .base-ag-grid-header {
      [style*="gap: 1rem"] {
        gap: 0.875rem !important; // 14px
      }
      [style*="gap: 0.75rem"] {
        gap: 0.625rem !important; // 10px
      }
    }
  }
}

@media (min-width: 1025px) {
  // Desktop spacing - optimal
  .vx-card {
    .base-ag-grid-header {
      [style*="gap: 1rem"] {
        gap: 1.25rem !important; // 20px
      }
      [style*="gap: 0.75rem"] {
        gap: 0.875rem !important; // 14px
      }
    }
  }
}

// Mobile Sticky Bottom Bar (icon-only buttons)
.sticky-bottom-bar-icons {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #E5E7EB;
  padding: 8px 12px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100; // Increased z-index to ensure it stays above content
  margin: 0 -1.5rem -1.5rem; // Compensate for vx-card padding
  
  // Ensure sticky positioning works properly
  @media (max-width: 767px) {
    position: -webkit-sticky; // For Safari compatibility
    position: sticky;
  }
}

.mobile-bar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;
  color: #6B7280;
  font-family: inherit;
  outline: none;

  &:active {
    transform: scale(0.95);
    background: rgba(139, 92, 246, 0.1);
  }
}

.mobile-bar-btn-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.mobile-bar-icon {
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px auto;
  width: 22px;
  height: 22px;
}

.mobile-btn-label {
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  line-height: 1;
}

// Simple buttons (Form, Config, Refresh)
.mobile-bar-btn-simple {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }
}

// Refresh button - ensure no persistent background
.mobile-bar-btn-refresh {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }

  &:not(:active):not(:hover) {
    background: transparent !important;
  }
}

// Fix for desktop buttons with tooltips on touch devices
::v-deep .vs-button {
  -webkit-tap-highlight-color: transparent; // Remove tap highlight
  
  // Fix for flat buttons on touch - override Vuesax default styles
  &.vs-button-flat {
    // Remove all backgrounds on touch devices
    @media (hover: none) and (pointer: coarse) {
      background: transparent !important;
      background-color: transparent !important;
      
      &::before,
      &::after {
        display: none !important; // Remove Vuesax's background effect layers
      }
      
      // Prevent icon color changes
      .vs-button--icon,
      i,
      svg,
      .feather {
        color: rgba(var(--vs-primary), 1) !important;
        fill: rgba(var(--vs-primary), 1) !important; 
        stroke: rgba(var(--vs-primary), 1) !important;
        opacity: 1 !important;
      }
      
      &:active {
        background: rgba(139, 92, 246, 0.05) !important; // Very subtle touch feedback
        transition: background 0.1s ease;
        
        // Keep icon color stable during press
        .vs-button--icon,
        i,
        svg,
        .feather {
          color: rgba(var(--vs-primary), 1) !important;
          fill: rgba(var(--vs-primary), 1) !important;
          stroke: rgba(var(--vs-primary), 1) !important;
          opacity: 1 !important;
        }
      }
      
      &:not(:active) {
        background: transparent !important;
        background-color: transparent !important;
        transition: background 0.1s ease;
      }
    }
    
    // Only apply hover on hover-capable devices
    @media (hover: hover) {
      &:hover {
        background: rgba(var(--vs-primary), 0.1);
      }
    }
  }
  
  // General button fixes for touch
  @media (hover: none) and (pointer: coarse) {
    &:not(.vs-button-flat):active {
      transition: background-color 0.1s ease;
    }
    
    &:not(.vs-button-flat):not(:active) {
      background-color: initial !important;
      transition: background-color 0.1s ease;
    }
  }
}

// Special fix for icon-only flat buttons in the header
@media (hover: none) and (pointer: coarse) {
  .base-ag-grid-header ::v-deep .vs-button.vs-button-flat.vs-radius {
    background: transparent !important;
    background-color: transparent !important;
    
    // Remove all Vuesax effects
    &::before,
    &::after,
    .vs-button--background,
    .vs-button--backgroundx {
      display: none !important;
      opacity: 0 !important;
      background: transparent !important;
    }
    
    // Keep icon color consistent - never change it
    .vs-button--icon,
    i,
    svg,
    .feather {
      color: rgba(var(--vs-primary), 1) !important;
      fill: rgba(var(--vs-primary), 1) !important;
      stroke: rgba(var(--vs-primary), 1) !important;
      opacity: 1 !important;
    }
    
    &:active {
      background: rgba(139, 92, 246, 0.08) !important; // Subtle feedback matching mobile buttons
      
      // Ensure icon stays the same color when active
      .vs-button--icon,
      i,
      svg,
      .feather {
        color: rgba(var(--vs-primary), 1) !important;
        fill: rgba(var(--vs-primary), 1) !important;
        stroke: rgba(var(--vs-primary), 1) !important;
        opacity: 1 !important;
      }
    }
    
    &:not(:active) {
      background: transparent !important;
      
      // Ensure icon stays the same color when not active
      .vs-button--icon,
      i,
      svg,
      .feather {
        color: rgba(var(--vs-primary), 1) !important;
        fill: rgba(var(--vs-primary), 1) !important;
        stroke: rgba(var(--vs-primary), 1) !important;
        opacity: 1 !important;
      }
    }
  }
}

// Hide all tooltips on touch devices
@media (hover: none) and (pointer: coarse) {
  ::v-deep .vx-tooltip .vs-tooltip,
  ::v-deep .con-vs-tooltip .vs-tooltip {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
  
  // Prevent tooltip activation on touch
  ::v-deep .con-vs-tooltip {
    &::before,
    &::after {
      display: none !important;
    }
  }
}

// Fix vs-dropdown touch highlighting issues  
::v-deep .vs-dropdown {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  
  // Ensure dropdown trigger doesn't get stuck highlighted
  .vs-dropdown--custom {
    -webkit-tap-highlight-color: transparent;
  }
}

// Additional fix for the interactive pill wrapper
.interactive-pill {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  
  // Prevent any default touch highlighting
  * {
    -webkit-tap-highlight-color: transparent;
  }
}

// Colored action buttons
.mobile-bar-btn-danger {
  background: rgba(var(--vs-danger), 1);
  color: white;

  &:hover {
    background: rgba(var(--vs-danger), 0.9);
  }

  &:active {
    background: rgba(var(--vs-danger), 0.8);
    transform: scale(0.95);
  }
}

.mobile-bar-btn-primary {
  background: rgba(var(--vs-primary), 1);
  color: white;

  &:hover {
    background: rgba(var(--vs-primary), 0.9);
  }

  &:active {
    background: rgba(var(--vs-primary), 0.8);
    transform: scale(0.95);
  }
}

.mobile-bar-btn-success {
  background: rgba(var(--vs-success), 1);
  color: white;

  &:hover {
    background: rgba(var(--vs-success), 0.9);
  }

  &:active {
    background: rgba(var(--vs-success), 0.8);
    transform: scale(0.95);
  }
}

.mobile-status-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: block;
  margin: 0 auto 6px auto;
  cursor: pointer;

  &.status-closed {
    background: rgba(var(--vs-danger), 1);
  }

  &.status-active {
    background: rgba(var(--vs-success), 1);
    animation: pulse 2s infinite;
  }

  &.status-paused {
    background: #9CA3AF;
  }

  &.status-pending {
    background: rgba(var(--vs-warning), 1);
  }
}
</style>
